import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { colors } from '../Colors';
import { musicApi } from '../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MusicNote as MusicNoteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

function MusicsScreen() {
  const [musics, setMusics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newMusicFile, setNewMusicFile] = useState(null);
  const [newMusicTitle, setNewMusicTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Fetch musics on component mount
  useEffect(() => {
    fetchMusics();
  }, []);

  const fetchMusics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await musicApi.getAllMusics();
      if (response.success && response.data && response.data.musics) {
        setMusics(response.data.musics);
      } else {
        setError('Erreur lors du chargement des musiques');
        setMusics([]);
      }
    } catch (err) {
      console.error('Error fetching musics:', err);
      setError('Erreur lors du chargement des musiques: ' + err.message);
      setMusics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setNewMusicFile(null);
    setNewMusicTitle('');
    setError(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewMusicFile(null);
    setNewMusicTitle('');
    setError(null);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix (data:audio/mpeg;base64,) and keep only the base64 string
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAddMusic = async () => {
    if (!newMusicFile) {
      setError('Veuillez sélectionner un fichier MP3');
      return;
    }

    // Validate file type - check MIME type or file extension
    const validMimeTypes = ['audio/mpeg', 'audio/mp3', 'audio/mpeg3', 'audio/x-mpeg-3'];
    const validExtensions = ['.mp3', '.MP3'];
    const fileName = newMusicFile.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!validMimeTypes.includes(newMusicFile.type) && !hasValidExtension) {
      setError('Veuillez sélectionner un fichier MP3 valide');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      // Convert file to base64
      const fileBase64 = await convertFileToBase64(newMusicFile);
      
      // Upload to API
      await musicApi.uploadMusic(
        newMusicTitle.trim() || 'Nouvelle Musique',
        fileBase64
      );
      
      // Refresh musics list
      await fetchMusics();
      handleCloseAddDialog();
    } catch (err) {
      console.error('Error uploading music:', err);
      setError('Erreur lors de l\'upload de la musique: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMusic = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette musique ?')) {
      return;
    }

    try {
      setError(null);
      await musicApi.deleteMusic(id);
      // Refresh musics list
      await fetchMusics();
    } catch (err) {
      console.error('Error deleting music:', err);
      setError('Erreur lors de la suppression de la musique: ' + err.message);
    }
  };

  const handleStartEdit = (music) => {
    setEditingId(music.id);
    setEditingTitle(music.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle('');
  };

  const handleSaveEdit = (id) => {
    // Note: Edit functionality is kept for UI consistency, but API doesn't support update
    // This is a local-only edit for display purposes
    setMusics(musics.map(music => 
      music.id === id 
        ? { ...music, name: editingTitle.trim() || music.name }
        : music
    ));
    setEditingId(null);
    setEditingTitle('');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background.primary }}>
      <Navigation />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MusicNoteIcon sx={{ fontSize: '2.5rem', color: colors.primary.main }} />
            <Typography variant="h4" sx={{ color: colors.text.primary, fontWeight: 'bold' }}>
              Musiques
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            disabled={loading}
            sx={{
              bgcolor: colors.primary.main,
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontSize: '1rem',
              fontWeight: 'bold',
              '&:hover': { 
                bgcolor: colors.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Ajouter une musique
          </Button>
        </Box>

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : musics.length === 0 ? (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <MusicNoteIcon sx={{ fontSize: '4rem', color: colors.text.light, mb: 2 }} />
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                Aucune musique
              </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 3 }}>
                Commencez par ajouter votre première musique
              </Typography>
              <Button
                variant="contained"
                onClick={handleOpenAddDialog}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: colors.primary.main,
                  borderRadius: '20px',
                  px: 3,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: colors.primary.dark }
                }}
              >
                Ajouter une musique
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {musics.map((music) => (
              <Grid item xs={12} sm={12} md={6} key={music.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    bgcolor: colors.background.secondary,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MusicNoteIcon sx={{ fontSize: '2rem', color: colors.primary.main, mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        {editingId === music.id ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              autoFocus
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(music.id);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              size="small"
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(music.id)}
                              sx={{
                                bgcolor: colors.primary.main,
                                color: 'white',
                                '&:hover': {
                                  bgcolor: colors.primary.dark
                                }
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              sx={{
                                color: colors.text.secondary,
                                '&:hover': {
                                  bgcolor: colors.background.primary
                                }
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 'bold',
                                flex: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  color: colors.primary.main
                                }
                              }}
                              onDoubleClick={() => handleStartEdit(music)}
                            >
                              {music.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(music)}
                              sx={{
                                color: colors.text.secondary,
                                '&:hover': {
                                  bgcolor: colors.background.primary,
                                  color: colors.primary.main
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ 
                      bgcolor: colors.background.primary, 
                      borderRadius: 2, 
                      p: 2, 
                      mb: 2,
                      flex: 1
                    }}>
                      <audio controls style={{ width: '100%' }}>
                        <source src={music.url} type="audio/mpeg" />
                        Votre navigateur ne supporte pas l'élément audio.
                      </audio>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton
                        onClick={() => handleDeleteMusic(music.id)}
                        sx={{
                          color: colors.error || '#E74C3C',
                          '&:hover': {
                            bgcolor: 'rgba(231, 76, 60, 0.1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Music Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              border: '2px solid #4A90E2'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2,
            background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
            color: 'white',
            borderRadius: '20px 20px 0 0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MusicNoteIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Ajouter une musique
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Titre de la musique"
              value={newMusicTitle}
              onChange={(e) => setNewMusicTitle(e.target.value)}
              placeholder="Ex: Chant Traditionnel"
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <input
                accept="audio/mpeg,audio/mp3"
                style={{ display: 'none' }}
                id="music-file-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setNewMusicFile(file);
                    setError(null);
                    // Auto-fill title if empty
                    if (!newMusicTitle.trim()) {
                      setNewMusicTitle(file.name.replace(/\.[^/.]+$/, ''));
                    }
                  }
                }}
              />
              <label htmlFor="music-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{
                    borderRadius: '20px',
                    py: 1.5,
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      bgcolor: 'rgba(74, 144, 226, 0.1)'
                    }
                  }}
                >
                  {newMusicFile ? newMusicFile.name : 'Sélectionner un fichier MP3'}
                </Button>
              </label>
            </Box>
            {newMusicFile && (
              <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
                Fichier sélectionné: {newMusicFile.name} ({(newMusicFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleCloseAddDialog}
              disabled={uploading}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                color: colors.text.secondary
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddMusic}
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              disabled={uploading || !newMusicFile}
              sx={{
                bgcolor: colors.primary.main,
                borderRadius: '20px',
                px: 3,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: colors.primary.dark
                },
                '&:disabled': {
                  bgcolor: colors.text.light
                }
              }}
            >
              {uploading ? 'Upload en cours...' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}

export default MusicsScreen;

